import { forwardRef, useImperativeHandle, useMemo, useEffect, useState } from "react";
import GoogleMap from "google-maps-react-markers";
import { MapMarker } from "./MapMarker";
import { MapOverlay } from "@/map/components/map-overlay";
import { useMapState, useMapPaths, useMapInit, useMapHeatmap } from "@/map/hooks";
import { toGoogleMapOptions } from "@/map/utils/map-view-options";
import { DEFAULT_ZOOM, MAP_LIBRARIES } from "@/map/utils/mapConstants";
import { activeLocale } from "@/shared/lib/i18n/formatters";
import type { MapCanvasProps, MapCanvasHandle } from "./map-canvas.types";

const GoogleMapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>(
  (
    {
      basicCoordinates,
      polygonCoordinates,
      options,
      center,
      height,
      overlay,
      overlayEnabled,
      onMarkerClick,
      onTooltipShow,
      onTooltipHide,
      onZoomChange,
      onInBoundsChange,
      emphasizedMarkerId,
      onMarkerHover,
    },
    ref,
  ) => {
    const allCoordinates = useMemo(
      () => [...basicCoordinates, ...polygonCoordinates],
      [basicCoordinates, polygonCoordinates],
    );

    // Reconstruct the overlayData shape `useMapState` expects (it only reads
    // `data.available` + `data.config.bounds` for the in-bounds intersection
    // check). Keeps the original Google behavior verbatim.
    const overlayData = useMemo(
      () =>
        overlay
          ? { data: { available: true, config: { bounds: overlay.bounds } } }
          : undefined,
      [overlay],
    );

    const mapState = useMapState({ overlayData });
    const mapInit = useMapInit({
      mapRef: mapState.mapRef,
      mapsRef: mapState.mapsRef,
      allCoordinates,
      initialHeight: height,
      onZoomChanged: mapState.handleZoomChanged,
      onBoundsChanged: mapState.handleBoundsChanged,
    });

    useMapPaths({
      mapRef: mapState.mapRef,
      mapsRef: mapState.mapsRef,
      basicCoordinates,
      polygonCoordinates,
      onTooltipShow,
      onTooltipHide,
    });

    useMapHeatmap({
      mapRef: mapState.mapRef,
      mapsRef: mapState.mapsRef,
      basicCoordinates,
    });

    // Bubble zoom/bounds to the shell for the overlay controls.
    useEffect(() => onZoomChange?.(mapState.currentZoom), [mapState.currentZoom, onZoomChange]);
    useEffect(() => onInBoundsChange?.(mapState.isInBounds), [mapState.isInBounds, onInBoundsChange]);

    // Signals the map instance exists, so the maxZoom effect below can apply the
    // initial cap once the ref is populated (set from onGoogleApiLoaded).
    const [mapReady, setMapReady] = useState(false);

    useImperativeHandle(ref, () => ({
      centerOn: (lat: number, lng: number, zoom?: number) => mapState.centerOn(lat, lng, zoom),
      panTo: (lat: number, lng: number) => mapState.panTo(lat, lng),
      isInView: (lat: number, lng: number) => mapState.isInView(lat, lng),
      fitTo: (points) => mapState.fitTo(points),
    }));

    const googleOptions = useMemo(() => toGoogleMapOptions(options), [options]);
    const apiParams = useMemo(() => ({ language: activeLocale() }), []);

    // The <GoogleMap> library applies `options` only at map creation, so re-apply
    // `maxZoom` imperatively whenever it changes (e.g. an overlay turns on and
    // caps the zoom, or off and restores full zoom). Google clamps the current
    // zoom to a lowered cap immediately. Gated on `mapReady` so the ref exists.
    useEffect(() => {
      const map = mapState.mapRef.current;
      if (!map) return;
      map.setOptions({ maxZoom: googleOptions.maxZoom ?? null });
      // mapState.mapRef is a stable ref; mapReady drives the initial apply.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady, googleOptions.maxZoom]);

    return (
      <>
        <GoogleMap
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
          libraries={MAP_LIBRARIES}
          // Without this Google renders its own controls, attribution and
          // place names in the BROWSER's language, so an English or pt-BR app
          // can show a map in whatever the machine happens to be set to.
          // These are appended to the API script URL, which Google loads and
          // caches once, so the value is read at mount: a language change
          // takes effect on the next reload.
          externalApiParams={apiParams}
          defaultCenter={center}
          defaultZoom={googleOptions.zoom ?? DEFAULT_ZOOM}
          options={googleOptions}
          onGoogleApiLoaded={(args) => {
            mapInit.handleGoogleApiLoaded(args);
            setMapReady(true);
          }}
        >
          {allCoordinates.flatMap(
            ({ name, path, color, isActive, disableShadow, markerType, isReviewed, isSelected, selectionId, zIndex }, setIndex) =>
              markerType !== "gps" &&
              (path.length === 1 || basicCoordinates.some((coord) => coord.name === name))
                ? path.map((point, idx) => {
                    const markerId = selectionId ?? `${name}-${idx}`;
                    return (
                      <MapMarker
                        key={`${setIndex}-${selectionId ?? name}-${idx}`}
                        lat={point.lat}
                        lng={point.lng}
                        markerId={markerId}
                        label={name}
                        onClick={(_e, data) =>
                          onMarkerClick({
                            ...data,
                            time: point.time ? new Date(point.time) : undefined,
                          })
                        }
                        color={color}
                        isActive={isActive}
                        isLast={idx === path.length - 1}
                        time={point.time}
                        disableShadow={disableShadow}
                        count={point.count}
                        markerType={markerType}
                        isReviewed={isReviewed}
                        isSelected={isSelected}
                        zIndex={zIndex}
                        dimmed={
                          emphasizedMarkerId != null &&
                          markerType === "alert" &&
                          markerId !== emphasizedMarkerId
                        }
                        onHover={markerType === "alert" ? onMarkerHover : undefined}
                      />
                    );
                  })
                : [],
          )}
        </GoogleMap>
        {overlay && overlayEnabled && (
          <MapOverlay map={mapState.mapRef.current} config={overlay} />
        )}
      </>
    );
  },
);
GoogleMapCanvas.displayName = "GoogleMapCanvas";
export default GoogleMapCanvas;
